import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Box, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PracticeIDE from './components/PracticeIDE';
import {
  getStudentAppApiBaseUrl,
  getStudentAuthHeaders,
  loadFallbackRunContentId,
} from './utils/practiceIdeUtils';

/**
 * Full-screen student Practice IDE — Programiz-style editor + output split layout.
 * Route: /student/courses/:courseId/practice-ide
 */
export default function PracticeIdeScreen() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navFallbackId = location.state?.fallbackRunContentId ?? null;
  const [fallbackRunContentId, setFallbackRunContentId] = useState(navFallbackId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${getStudentAppApiBaseUrl()}/lms/courses/${courseId}/builder`,
          {
            headers: getStudentAuthHeaders(),
            params: { publishedOnly: true },
          },
        );
        const allowed = response?.data?.showPracticeIde === true;
        if (!cancelled && !allowed) {
          navigate(`/student/courses/${courseId}`, { replace: true });
        }
      } catch {
        if (!cancelled) {
          navigate(`/student/courses/${courseId}`, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, navigate]);

  useEffect(() => {
    if (navFallbackId != null) {
      setFallbackRunContentId(navFallbackId);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const id = await loadFallbackRunContentId(courseId);
        if (!cancelled) setFallbackRunContentId(id);
      } catch {
        if (!cancelled) setFallbackRunContentId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, navFallbackId]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          minHeight: 40,
          bgcolor: '#F8F9FA',
          borderBottom: '1px solid #E4E7EB',
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate(`/student/courses/${courseId}`)}
          sx={{ color: '#374151' }}
          aria-label="Back to course"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <PracticeIDE
          persistenceKey={courseId}
          fallbackRunContentId={fallbackRunContentId}
          embedded
        />
      </Box>
    </Box>
  );
}
